import React, { useCallback, useEffect, useRef, useState } from "react";
import { Mic, Volume2 } from "lucide-react";
import { SecondaryButton, Size } from "@ever-tutor/et-ui";
import Modal, { ModalSize } from "./Modal";
import PrimaryButton from "../PrimaryButton";
import {
  loadPreferredMicDeviceId,
  savePreferredMicDeviceId,
} from "@/features/tutor/vo-demo/audio/microphone";
import "./lesson-entry.css";

const TEST_RECORD_DURATION_MS = 3000;
const SPEAKER_TEST_AUDIO_URL = `${import.meta.env.VITE_S3_BUCKET_URL ?? ""}/voice_options_audio/speaker-test.mp3`;

type Stage = "speaker" | "recording" | "playback";

// HTMLAudioElement.setSinkId is Chromium-only. Type narrowing helper for the
// "Switch speakers" button.
type AudioWithSinkId = HTMLAudioElement & {
  setSinkId?: (deviceId: string) => Promise<void>;
};
const supportsSetSinkId = (() => {
  if (typeof window === "undefined") return false;
  return (
    "setSinkId" in HTMLMediaElement.prototype ||
    "setSinkId" in HTMLAudioElement.prototype
  );
})();

interface MicSilenceTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  embedded?: boolean;
  // Hooks the production audio graph up so it re-inits with the new deviceId.
  onDeviceConfirmed?: (deviceId: string) => void;
}

const MicSilenceTestModal: React.FC<MicSilenceTestModalProps> = ({
  isOpen,
  onClose,
  onDeviceConfirmed,
  embedded = false,
}) => {
  const [micDevices, setMicDevices] = useState<MediaDeviceInfo[]>([]);
  const [speakerDevices, setSpeakerDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<string>("");
  const [stage, setStage] = useState<Stage>("speaker");
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [recordCountdown, setRecordCountdown] = useState(
    Math.ceil(TEST_RECORD_DURATION_MS / 1000),
  );
  const [recordAmplitude, setRecordAmplitude] = useState(0);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [speakerControlsVisible, setSpeakerControlsVisible] = useState(false);
  const [micSwitchVisible, setMicSwitchVisible] = useState(false);
  // Gates the speaker test (and downstream stages) on a granted mic permission
  // so we don't try to play setSinkId-routed audio before the user has decided.
  const [micPermissionGranted, setMicPermissionGranted] = useState(false);
  // Set when audio.play() is rejected by the browser (autoplay policy / no
  // available output / user-gesture requirement). Treated like a blocked
  // permission: gate the flow until the user retries via "Try again".
  const [speakerBlocked, setSpeakerBlocked] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const recordTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recordProgressRafRef = useRef<number | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const speakerAudioRef = useRef<HTMLAudioElement | null>(null);
  const playbackAudioRef = useRef<HTMLAudioElement | null>(null);
  // Bumped whenever we want the speaker-stage effect to re-run (e.g. user
  // clicked "Switch speakers" and we want to replay the test sound).
  const [speakerPlayNonce, setSpeakerPlayNonce] = useState(0);
  // Guards async callbacks (recorder.onstop, audio.onended) firing after the
  // modal has closed.
  const isOpenRef = useRef(isOpen);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const stopMediaStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const teardown = useCallback(() => {
    if (recordTimeoutRef.current) {
      clearTimeout(recordTimeoutRef.current);
      recordTimeoutRef.current = null;
    }
    if (recordProgressRafRef.current !== null) {
      cancelAnimationFrame(recordProgressRafRef.current);
      recordProgressRafRef.current = null;
    }
    if (recorderRef.current?.state === "recording") {
      try {
        recorderRef.current.stop();
      } catch {
        /* noop */
      }
    }
    recorderRef.current = null;
    if (analyserRef.current) {
      try {
        analyserRef.current.disconnect();
      } catch {
        /* noop */
      }
      analyserRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    stopMediaStream();
    if (speakerAudioRef.current) {
      speakerAudioRef.current.onended = null;
      speakerAudioRef.current.onerror = null;
      speakerAudioRef.current.pause();
      speakerAudioRef.current = null;
    }
    if (playbackAudioRef.current) {
      playbackAudioRef.current.onended = null;
      playbackAudioRef.current.onerror = null;
      playbackAudioRef.current.pause();
      playbackAudioRef.current = null;
    }
  }, [stopMediaStream]);

  const startMicRecording = useCallback(async (deviceId: string) => {
    if (!isOpenRef.current) return;
    setStreamError(null);
    setMicSwitchVisible(false);
    setRecordAmplitude(0);
    setRecordCountdown(Math.ceil(TEST_RECORD_DURATION_MS / 1000));
    setRecordedUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    recordedChunksRef.current = [];
    setStage("recording");

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      if (!isOpenRef.current) return;
      setStreamError(
        (err instanceof Error ? err.name : "") === "NotAllowedError"
          ? "Microphone permission was denied for this device."
          : "Couldn't access this microphone.",
      );
      return;
    }
    if (!isOpenRef.current) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    streamRef.current = stream;

    // Wire stream into an AnalyserNode so we can render an amplitude meter.
    try {
      type AudioCtxCtor = typeof AudioContext;
      const Ctx: AudioCtxCtor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: AudioCtxCtor })
          .webkitAudioContext;
      const ctx = new Ctx();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
    } catch {
      // If analyser setup fails, the meter just stays at 0 — recording still
      // works.
    }

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream);
    } catch {
      setStreamError("Recording isn't supported in this browser.");
      return;
    }
    recorder.ondataavailable = (e) => {
      if (e.data?.size) recordedChunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      if (recordProgressRafRef.current !== null) {
        cancelAnimationFrame(recordProgressRafRef.current);
        recordProgressRafRef.current = null;
      }
      recordTimeoutRef.current = null;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      if (analyserRef.current) {
        try {
          analyserRef.current.disconnect();
        } catch {
          /* noop */
        }
        analyserRef.current = null;
      }
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
      if (!isOpenRef.current) return;
      const blob = new Blob(recordedChunksRef.current, {
        type: recorder.mimeType || "audio/webm",
      });
      setRecordedUrl(URL.createObjectURL(blob));
      setStage("playback");
    };
    recorderRef.current = recorder;
    recorder.start();

    const startedAt = performance.now();
    const buffer = new Uint8Array(analyserRef.current?.fftSize ?? 1024);
    const tickProgress = () => {
      const elapsed = performance.now() - startedAt;
      const remainingMs = Math.max(0, TEST_RECORD_DURATION_MS - elapsed);
      setRecordCountdown(Math.ceil(remainingMs / 1000));
      if (analyserRef.current) {
        analyserRef.current.getByteTimeDomainData(buffer);
        // RMS deviation from 128 (silence midpoint), normalized to [0,1].
        let sumSq = 0;
        for (let i = 0; i < buffer.length; i++) {
          const v = (buffer[i] - 128) / 128;
          sumSq += v * v;
        }
        const rms = Math.sqrt(sumSq / buffer.length);
        // Boost so quiet speech reads visually; clamp at 1.
        setRecordAmplitude(Math.min(1, rms * 3));
      }
      if (elapsed < TEST_RECORD_DURATION_MS) {
        recordProgressRafRef.current = requestAnimationFrame(tickProgress);
      } else {
        recordProgressRafRef.current = null;
      }
    };
    recordProgressRafRef.current = requestAnimationFrame(tickProgress);
    recordTimeoutRef.current = setTimeout(() => {
      if (recorderRef.current?.state === "recording") {
        recorderRef.current.stop();
      }
    }, TEST_RECORD_DURATION_MS);
  }, []);

  const refreshDevices = useCallback(async () => {
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = list.filter((d) => d.kind === "audioinput");
      const audioOutputs = list.filter((d) => d.kind === "audiooutput");
      setMicDevices(audioInputs);
      setSpeakerDevices(audioOutputs);
      setSelectedDeviceId((prev) => {
        if (audioInputs.some((d) => d.deviceId === prev)) return prev;
        const preferred = loadPreferredMicDeviceId();
        if (preferred && audioInputs.some((d) => d.deviceId === preferred)) {
          return preferred;
        }
        return audioInputs[0]?.deviceId || "";
      });
      setSelectedSpeakerId((prev) => {
        if (audioOutputs.some((d) => d.deviceId === prev)) return prev;
        return audioOutputs[0]?.deviceId || "";
      });
    } catch {
      setStreamError("Couldn't list audio devices in this browser.");
    }
  }, []);

  // Reset state on close; load devices on open.
  useEffect(() => {
    if (!isOpen) {
      teardown();
      setStage("speaker");
      setSpeakerControlsVisible(false);
      setMicSwitchVisible(false);
      setMicPermissionGranted(false);
      setSpeakerBlocked(false);
      setRecordAmplitude(0);
      setRecordCountdown(Math.ceil(TEST_RECORD_DURATION_MS / 1000));
      setStreamError(null);
      setSpeakerPlayNonce(0);
      setRecordedUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      recordedChunksRef.current = [];
      return;
    }
    // Check permission state without prompting. If already granted, we can
    // skip the gesture-gated request. Otherwise, the user must click the
    // "Allow microphone" button — browsers require a fresh user gesture to
    // show the prompt, and async work in this effect can lose that context.
    let cancelled = false;
    (async () => {
      try {
        const status = await navigator.permissions?.query?.({
          name: "microphone" as PermissionName,
        });
        if (cancelled || !isOpenRef.current) return;
        if (status?.state === "granted") {
          setMicPermissionGranted(true);
          refreshDevices();
          return;
        }
      } catch {
        // Permissions API unavailable (Safari < 16) — fall through to the
        // button-driven request.
      }
      if (cancelled || !isOpenRef.current) return;
      refreshDevices();
    })();
    const handler = () => refreshDevices();
    navigator.mediaDevices?.addEventListener?.("devicechange", handler);
    return () => {
      cancelled = true;
      navigator.mediaDevices?.removeEventListener?.("devicechange", handler);
    };
  }, [isOpen, refreshDevices, teardown]);

  // The settings sheet unmounts this panel while isOpen is still true, which
  // skips the close branch above. Release the mic and the recording here too.
  const recordedUrlRef = useRef(recordedUrl);
  recordedUrlRef.current = recordedUrl;
  useEffect(
    () => () => {
      isOpenRef.current = false;
      teardown();
      if (recordedUrlRef.current) URL.revokeObjectURL(recordedUrlRef.current);
    },
    [teardown],
  );

  // Explicit user-gesture path: every click fires a fresh getUserMedia, which
  // is required to show the browser prompt. If the browser has cached "denied"
  // it'll reject immediately — surface an error with OS-level guidance.
  const handleRequestMicPermission = useCallback(async () => {
    setStreamError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      if (!isOpenRef.current) return;
      setMicPermissionGranted(true);
      refreshDevices();
    } catch (err) {
      if (!isOpenRef.current) return;
      const name = err instanceof Error ? err.name : "";
      setStreamError(
        name === "NotAllowedError"
          ? "Microphone access is blocked. Enable it for this site in your browser's address-bar settings, then click again."
          : "Couldn't access the microphone. Make sure one is connected and try again.",
      );
    }
  }, [refreshDevices]);

  // Speaker stage: play the test sound. When it finishes (or fails), reveal
  // the "Switch speakers" / "Move onto mic test" buttons. User must click
  // through — no auto-advance.
  useEffect(() => {
    if (!isOpen || stage !== "speaker" || !micPermissionGranted) return;
    const audio = new Audio(SPEAKER_TEST_AUDIO_URL) as AudioWithSinkId;
    speakerAudioRef.current = audio;
    setSpeakerControlsVisible(false);
    setSpeakerBlocked(false);

    const reveal = () => {
      if (speakerAudioRef.current === audio) speakerAudioRef.current = null;
      if (isOpenRef.current) setSpeakerControlsVisible(true);
    };
    audio.onended = reveal;
    audio.onerror = reveal;

    const play = async () => {
      if (
        supportsSetSinkId &&
        selectedSpeakerId &&
        typeof audio.setSinkId === "function"
      ) {
        try {
          await audio.setSinkId(selectedSpeakerId);
        } catch {
          // setSinkId can fail without user permission for this output device.
          // Fall through to play on default speaker.
        }
      }
      try {
        await audio.play();
      } catch {
        // Browser blocked playback (autoplay policy, missing user gesture, no
        // available output). Gate the flow until the user retries.
        if (isOpenRef.current) setSpeakerBlocked(true);
        reveal();
      }
    };
    play();

    return () => {
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
      if (speakerAudioRef.current === audio) speakerAudioRef.current = null;
    };
  }, [
    isOpen,
    stage,
    selectedSpeakerId,
    speakerPlayNonce,
    micPermissionGranted,
  ]);

  // Playback stage: auto-play recorded audio, reveal switch button when done.
  useEffect(() => {
    if (!isOpen || stage !== "playback" || !recordedUrl) return;
    const audio = new Audio(recordedUrl) as AudioWithSinkId;
    playbackAudioRef.current = audio;
    const reveal = () => {
      if (playbackAudioRef.current === audio) playbackAudioRef.current = null;
      if (isOpenRef.current) setMicSwitchVisible(true);
    };
    audio.onended = reveal;
    audio.onerror = reveal;

    const play = async () => {
      if (
        supportsSetSinkId &&
        selectedSpeakerId &&
        typeof audio.setSinkId === "function"
      ) {
        try {
          await audio.setSinkId(selectedSpeakerId);
        } catch {
          /* fall through */
        }
      }
      try {
        await audio.play();
      } catch {
        reveal();
      }
    };
    play();

    return () => {
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
      if (playbackAudioRef.current === audio) playbackAudioRef.current = null;
    };
  }, [isOpen, stage, recordedUrl, selectedSpeakerId]);

  const handleSwitchSpeaker = useCallback(() => {
    if (speakerDevices.length < 2) return;
    const currentIdx = speakerDevices.findIndex(
      (d) => d.deviceId === selectedSpeakerId,
    );
    const nextIdx = (currentIdx + 1) % speakerDevices.length;
    setSelectedSpeakerId(speakerDevices[nextIdx].deviceId);
    // Re-trigger the speaker-stage effect to replay the test sound.
    setSpeakerPlayNonce((n) => n + 1);
  }, [speakerDevices, selectedSpeakerId]);

  const handleMoveToMicTest = useCallback(() => {
    if (!selectedDeviceId) return;
    startMicRecording(selectedDeviceId);
  }, [selectedDeviceId, startMicRecording]);

  const handleSwitchMic = useCallback(() => {
    if (micDevices.length === 0) return;
    const currentIdx = micDevices.findIndex(
      (d) => d.deviceId === selectedDeviceId,
    );
    const nextIdx = (currentIdx + 1) % micDevices.length;
    const nextDeviceId = micDevices[nextIdx].deviceId;
    setSelectedDeviceId(nextDeviceId);
    startMicRecording(nextDeviceId);
  }, [micDevices, selectedDeviceId, startMicRecording]);

  const handleConfirm = useCallback(() => {
    if (!selectedDeviceId) return;
    savePreferredMicDeviceId(selectedDeviceId);
    onDeviceConfirmed?.(selectedDeviceId);
    onClose();
  }, [selectedDeviceId, onDeviceConfirmed, onClose]);

  const currentMic = micDevices.find((d) => d.deviceId === selectedDeviceId);
  const micLabel = currentMic?.label || "Default microphone";
  const currentSpeaker = speakerDevices.find(
    (d) => d.deviceId === selectedSpeakerId,
  );
  const speakerLabel =
    currentSpeaker?.label ||
    (speakerDevices.length > 0 ? "Default speaker" : "System default");

  const stageMessage =
    stage === "speaker"
      ? !micPermissionGranted
        ? "Allow microphone access to start the test."
        : speakerBlocked
          ? "Allow speaker playback to start the test."
          : speakerControlsVisible
            ? "Did you hear the test sound?"
            : "Listen for the test sound…"
      : stage === "recording"
        ? "Say something — we'll play it back to you."
        : "Playing back what we heard…";

  const StageIcon = stage === "speaker" ? Volume2 : Mic;
  // Speaker-switch button is only useful in browsers that support setSinkId
  // and when the user has more than one output device.
  const speakerSwitchEnabled = supportsSetSinkId && speakerDevices.length >= 2;

  if (!isOpen) return null;
  const content = (
    <div
      className={
        embedded
          ? "et-settings-form flex flex-col gap-4"
          : "flex flex-col gap-4 p-6"
      }
    >
      <h2 className="font-mulish font-extrabold text-[24px] text-[#294B39]">
        Speaker and Mic Test
      </h2>

      <div className="flex items-center gap-3">
        <StageIcon
          className="w-5 h-5 text-[#28543D] flex-shrink-0"
          aria-hidden="true"
        />
        <p className="font-mulish font-medium text-[16px] text-[#294B39]">
          {stageMessage}
        </p>
      </div>

      {stage === "recording" && (
        <div className="flex items-center gap-3">
          <div
            className="flex-1 h-[10px] bg-[#ede8dc] rounded-full overflow-hidden"
            role="meter"
            aria-label="Microphone amplitude"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(recordAmplitude * 100)}
          >
            <div
              className="h-full bg-[#28543D] rounded-full"
              style={{
                width: `${Math.round(recordAmplitude * 100)}%`,
                transition: "width 80ms linear",
              }}
            />
          </div>
          <span
            className="font-mulish font-semibold text-[14px] text-[#294B39] tabular-nums w-[24px] text-right"
            aria-live="polite"
            aria-label={`${recordCountdown} seconds remaining`}
          >
            {recordCountdown}s
          </span>
        </div>
      )}

      <p className="font-mulish text-[12px] text-gray-500 -mt-1">
        {stage === "speaker" ? `Speaker: ${speakerLabel}` : `Mic: ${micLabel}`}
      </p>

      {streamError && (
        <p className="text-[13px] text-red-600 font-mulish">{streamError}</p>
      )}

      <div className="flex items-center justify-end gap-3 pt-1">
        {stage === "speaker" && speakerControlsVisible && (
          <SecondaryButton
            onClick={handleSwitchSpeaker}
            disabled={!speakerSwitchEnabled}
            size={Size.SM}
            aria-label="Switch to next speaker"
            className="!min-h-[42px] !rounded-[6px] !border-[#d4d3c5] !bg-transparent !px-4 !py-2.5 !text-[13px] !font-[650] !text-[#28543D] hover:!bg-[#edf0e5]"
          >
            Switch speaker
          </SecondaryButton>
        )}
        {stage === "playback" && micSwitchVisible && (
          <SecondaryButton
            onClick={handleSwitchMic}
            disabled={micDevices.length < 2}
            size={Size.SM}
            aria-label="Switch to next microphone"
            className="!min-h-[42px] !rounded-[6px] !border-[#d4d3c5] !bg-transparent !px-4 !py-2.5 !text-[13px] !font-[650] !text-[#28543D] hover:!bg-[#edf0e5]"
          >
            Switch mic
          </SecondaryButton>
        )}
        <PrimaryButton
          onClick={
            !micPermissionGranted
              ? handleRequestMicPermission
              : stage === "speaker"
                ? speakerBlocked
                  ? () => setSpeakerPlayNonce((n) => n + 1)
                  : handleMoveToMicTest
                : handleConfirm
          }
          disabled={
            micPermissionGranted && (!selectedDeviceId || !!streamError)
          }
          size="sm"
        >
          {!micPermissionGranted
            ? "Allow microphone"
            : stage === "speaker"
              ? speakerBlocked
                ? "Try again"
                : "Use this speaker"
              : "Use this microphone"}
        </PrimaryButton>
      </div>
    </div>
  );
  return embedded ? (
    content
  ) : (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={ModalSize.sm}
      className="et-lesson-entry-modal et-entry-picker"
    >
      {content}
    </Modal>
  );
};

export default MicSilenceTestModal;
